import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { isWebsiteBuilderContainer, TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNodeGeometry";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { TWebsiteBuilderDevice } from "pages/secure/facility/websiteBuilder/editor/website/shared/device";

export interface IResizeContainerActionPayload {
  id: string;
  device: TWebsiteBuilderDevice;
  prevPosition: IWebsiteBuilderNodePosition;
  nextPosition: IWebsiteBuilderNodePosition;
  prevDimensions: IWebsiteBuilderNodeDimensions;
  nextDimensions: IWebsiteBuilderNodeDimensions;
}

export interface IResizeContainerAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.RESIZE_CONTAINER;
  payload: IResizeContainerActionPayload;
}

export function getResizeContainerInverseAction(action: IResizeContainerAction): IResizeContainerAction {
  const { id, device, nextPosition, prevPosition, nextDimensions, prevDimensions } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.RESIZE_CONTAINER,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      device,
      prevPosition: { ...nextPosition },
      nextPosition: { ...prevPosition },
      prevDimensions: { ...nextDimensions },
      nextDimensions: { ...prevDimensions },
    },
  };
}

type TResizeContainerTraversalNode = TWebsiteBuilderContainer | IWebsiteBuilderGeneralTraversalNode;

function resizeContainerTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  id: string,
  device: TWebsiteBuilderDevice,
  nextPosition: IWebsiteBuilderNodePosition,
  nextDimensions: IWebsiteBuilderNodeDimensions,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TResizeContainerTraversalNode): TResizeContainerTraversalNode {
    if (isWebsiteBuilderContainer(node)) {
      if (node.id === id) {
        return {
          ...node,
          container: {
            ...node.container,
            layout: {
              ...node.container.layout,
              [device]: {
                ...node.container.layout[device],
                position: nextPosition,
                dimensions: nextDimensions,
              },
            },
          },
        };
      } else {
        return node;
      }
    } else {
      if (!node.children) {
        return node;
      } else {
        return {
          ...node,
          children: node.children.map(traverse),
        };
      }
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applyResizeContainerChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IResizeContainerAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, device, nextPosition, nextDimensions } = action.payload;

  return resizeContainerTraversal(
    header,
    root,
    footer,
    id,
    device,
    { ...nextPosition },
    { ...nextDimensions },
  );
}
